import React from 'react';
import Layout from '@/components/Layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Cloud, 
  Shield, 
  Phone, 
  Clock,
  CheckCircle,
  AlertTriangle,
  FileText,
  Camera,
  Umbrella,
  Zap,
  Wind,
  CloudRain
} from 'lucide-react';

const StormDamage: React.FC = () => {
  const handleEmergencyCall = () => {
    window.location.href = 'tel:252-371-2266';
  };

  const handleGetHelp = () => {
    window.open('https://carolinatrophyroofs.sky-quote.com/schedule#step1', '_blank');
  };

  const stormTypes = [
    {
      icon: Wind,
      name: 'Hurricane Damage',
      description: 'High winds, flying debris, and heavy rain can cause severe roof damage requiring immediate attention.'
    },
    {
      icon: Cloud,
      name: 'Hail Damage',
      description: 'Hail storms can crack shingles, dent gutters, and compromise your roof\'s protective barrier.'
    },
    {
      icon: Zap,
      name: 'Wind Damage',
      description: 'Strong winds can lift shingles, damage flashing, and expose your home to water infiltration.'
    },
    {
      icon: CloudRain,
      name: 'Severe Thunderstorms',
      description: 'Heavy rains and lightning can cause immediate and long-term damage to roofing systems.'
    }
  ];

  const emergencySteps = [
    {
      step: '1',
      title: 'Ensure Safety First',
      description: 'Stay away from damaged areas and avoid going on the roof. Check for electrical hazards.'
    },
    {
      step: '2',
      title: 'Document the Damage',
      description: 'Take photos of visible damage from the ground and inside your home for insurance purposes.'
    },
    {
      step: '3',
      title: 'Contact Your Insurance',
      description: 'Report the damage to your insurance company as soon as possible to start the claims process.'
    },
    {
      step: '4',
      title: 'Call Carolina Trophy Roofs',
      description: 'Get professional emergency assessment and temporary protection to prevent further damage.'
    }
  ];

  const insuranceServices = [
    {
      icon: FileText,
      title: 'Insurance Claim Assistance',
      description: 'Complete support throughout the insurance claims process, from initial documentation to final approval.'
    },
    {
      icon: Camera,
      title: 'Damage Documentation',
      description: 'Professional documentation and photography of storm damage for insurance claim submissions.'
    },
    {
      icon: Shield,
      title: 'Emergency Tarping',
      description: 'Immediate temporary protection to prevent further water damage while waiting for insurance approval.'
    },
    {
      icon: Umbrella,
      title: 'Direct Insurance Billing',
      description: 'We work directly with your insurance company to streamline the repair process and payment.'
    }
  ];

  const warningSigns = [
    'Missing or damaged shingles',
    'Granules in gutters or yard',
    'Dented gutters or downspouts',
    'Water stains on ceilings or walls',
    'Damaged or exposed flashing',
    'Cracked or broken roof tiles',
    'Sagging or uneven roof lines',
    'Debris punctures or impacts'
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
        {/* Emergency Hero Section */}
        <section className="relative py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-red-600 to-red-800 text-white">
          <div className="max-w-7xl mx-auto text-center">
            <Badge variant="outline" className="mb-6 px-4 py-2 text-sm font-medium border-yellow-400 text-yellow-400">
              <AlertTriangle className="w-4 h-4 mr-2" />
              24/7 Emergency Response
            </Badge>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Storm Damage
              <span className="text-yellow-400 block mt-2">Emergency Roof Repair</span>
            </h1>
            
            <p className="text-xl text-red-100 max-w-4xl mx-auto mb-8">
              When severe weather strikes Eastern North Carolina, Carolina Trophy Roofs is ready to respond. 
              We provide 24/7 emergency storm damage repair and complete insurance claim assistance.
            </p>

            <Alert className="max-w-2xl mx-auto mb-8 bg-yellow-100 border-yellow-400">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              <AlertDescription className="text-yellow-800 font-semibold">
                <strong>EMERGENCY SITUATION?</strong> Don't wait - call us immediately at (252) 371-2266 for urgent storm damage response.
              </AlertDescription>
            </Alert>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button 
                onClick={handleEmergencyCall}
                size="lg"
                className="bg-yellow-400 hover:bg-yellow-500 text-red-800 font-bold px-8 py-3 text-lg"
              >
                <Phone className="w-6 h-6 mr-2" />
                EMERGENCY: (252) 371-2266
              </Button>
              <Button 
                onClick={handleGetHelp}
                variant="outline"
                size="lg"
                className="border-white text-black hover:bg-white hover:text-red-800 font-semibold px-8 py-3"
              >
                <Clock className="w-5 h-5 mr-2" />
                Schedule Assessment
              </Button>
            </div>

            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-400 mb-2">Available 24/7/365</div>
              <div className="text-red-100">Emergency storm damage response across Eastern NC</div>
            </div>
          </div>
        </section>

        {/* Storm Types */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Common Storm Damage in North Carolina
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Eastern North Carolina faces unique weather challenges. We're experienced in repairing damage from all types of severe weather events.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {stormTypes.map((storm, index) => (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow border-2 border-gray-100">
                  <CardHeader>
                    <storm.icon className="w-12 h-12 text-red-600 mx-auto mb-4" />
                    <CardTitle className="text-xl text-gray-900">{storm.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{storm.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Emergency Steps */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                What to Do After Storm Damage
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Follow these critical steps immediately after discovering storm damage to your roof.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {emergencySteps.map((step, index) => (
                <Card key={index} className="relative bg-white border-2 border-red-100">
                  <div className="absolute -top-4 left-4 w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold">
                    {step.step}
                  </div>
                  <CardHeader className="pt-8">
                    <CardTitle className="text-lg text-gray-900">{step.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{step.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Insurance Services */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Complete Insurance Claim Support
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                We handle all aspects of your insurance claim, from initial documentation to final repairs, 
                ensuring you get the coverage you deserve.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {insuranceServices.map((service, index) => (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <service.icon className="w-12 h-12 text-brand-trophy-gold mx-auto mb-4" />
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{service.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Warning Signs */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-yellow-50 border-t-4 border-yellow-400">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <AlertTriangle className="w-16 h-16 text-yellow-600 mx-auto mb-4" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Warning Signs of Storm Damage
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Don't wait for leaks to appear. Look for these signs of storm damage that require immediate professional attention.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {warningSigns.map((sign, index) => (
                <div key={index} className="flex items-center bg-white p-4 rounded-lg shadow-sm">
                  <CheckCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">{sign}</span>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Alert className="max-w-2xl mx-auto bg-red-50 border-red-200">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <AlertDescription className="text-red-800">
                  <strong>Don't Ignore the Signs:</strong> Even minor storm damage can lead to major problems if left untreated. 
                  Call for a professional inspection today.
                </AlertDescription>
              </Alert>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
              24/7 Emergency Storm Response Areas
            </h2>
            <p className="text-lg text-gray-600 mb-12">
              We provide emergency storm damage response throughout Eastern North Carolina
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[
                'Rocky Mount, NC', 'Greenville, NC', 'Wilson, NC', 'Goldsboro, NC',
                'Raleigh, NC', 'Jacksonville, NC', 'Nash County', 'Edgecombe County',
                'Pitt County', 'Wayne County', 'Wake County', 'Onslow County'
              ].map((area, index) => (
                <div key={index} className="bg-white p-3 rounded-lg shadow-sm border border-red-100">
                  <CheckCircle className="w-4 h-4 text-red-600 mx-auto mb-1" />
                  <span className="text-sm font-medium text-gray-700">{area}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reference Sources */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Authoritative Storm Damage & Insurance Resources
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { name: 'National Weather Service - Storm Safety', url: 'https://www.weather.gov/safety/' },
                { name: 'FEMA - Disaster Recovery', url: 'https://www.fema.gov/disaster-recovery' },
                { name: 'NC Department of Insurance', url: 'https://www.ncdoi.gov/consumers' },
                { name: 'Insurance Information Institute', url: 'https://www.iii.org/article/hurricanes-and-wind-damage' },
                { name: 'National Roofing Contractors Association', url: 'https://www.nrca.net/consumer/storm-damage' },
                { name: 'American Red Cross - Storm Preparation', url: 'https://www.redcross.org/get-help/how-to-prepare-for-emergencies/types-of-emergencies' },
                { name: 'National Hurricane Center', url: 'https://www.nhc.noaa.gov/' },
                { name: 'NC Emergency Management', url: 'https://www.ncdps.gov/emergency-management' }
              ].map((ref, index) => (
                <a 
                  key={index}
                  href={ref.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-4 bg-white border border-gray-200 rounded-lg hover:border-red-400 hover:shadow-md transition-all"
                >
                  <div className="text-sm font-medium text-brand-charcoal-gray hover:text-red-600">
                    {ref.name}
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Emergency CTA */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-red-600 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-3xl font-bold mb-4">
              Don't Let Storm Damage Get Worse
            </h3>
            <p className="text-xl text-red-100 mb-8">
              Every minute counts after storm damage. Our emergency response team is standing by 24/7 
              to protect your property and help you through the insurance process.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={handleEmergencyCall}
                size="lg"
                className="bg-yellow-400 hover:bg-yellow-500 text-red-800 font-bold px-8 py-3 text-lg"
              >
                <Phone className="w-6 h-6 mr-2" />
                CALL NOW: (252) 371-2266
              </Button>
              <Button 
                onClick={handleGetHelp}
                variant="outline"
                size="lg"
                className="border-white text-black hover:bg-white hover:text-red-800 font-semibold px-8 py-3"
              >
                Request Emergency Service
              </Button>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default StormDamage;